<script lang="ts">
  import { onDestroy, onMount, untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { mdiContentSave } from "@mdi/js"
  import { newApproval } from "@kontextwerk/web-sdk/core/overlay/approvals"
  import { registerNavigationGuard } from "@kontextwerk/web-sdk/core/routing/navigation"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import {
    type KehrblattPlanningSettings,
    type AppointmentLetterTodoAssignmentMode,
    type PlanningSettingsResponse,
  } from "../../../../../../../../api/kehrblatt/planning"
  import {
    appointmentLetterTodoAssignmentModeOptions,
    buildPlanningSettingsPayload,
    appointmentLetterLeadDayOptions,
    appointmentLetterLeadExample,
    customerAppointmentWindowDeltaOptions,
    customerAppointmentWindowRoundingOptions,
    flexSlotMinuteOptions,
    flexSlotsPerDayOptions,
    withCurrentNumberOption,
  } from "./_logic/planningSettings"

  const { organizationId, settings, canManage, onSettingsUpdated, saveSettings } = $props<{
    organizationId: string
    settings: PlanningSettingsResponse
    canManage: boolean
    saveSettings: (payload: KehrblattPlanningSettings) => Promise<PlanningSettingsResponse>
    onSettingsUpdated: (settings: PlanningSettingsResponse) => void
  }>()

  const readDraft = (source: PlanningSettingsResponse) => ({
    flexSlotMinutes: String(source.flex_slot_minutes ?? 60),
    flexSlotsPerDay: String(source.flex_slots_per_day ?? 0),
    customerAppointmentWindowDeltaMinutes: String(
      source.customer_appointment_window_delta_minutes ?? 30,
    ),
    customerAppointmentWindowRoundingMinutes: String(
      source.customer_appointment_window_rounding_minutes ?? 0,
    ),
    appointmentLetterLeadDays: String(source.appointment_letter_lead_days ?? 7),
    appointmentLetterAutoDispatchEnabled: source.appointment_letter_auto_dispatch_enabled ?? false,
    appointmentLetterTodoAssignmentMode:
      source.appointment_letter_todo_assignment_mode ?? "COLLECTIVE",
  })

  const initialDraft = untrack(() => readDraft(settings))
  let active = true
  onDestroy(() => {
    active = false
  })

  let saving = $state(false)
  let flexSlotMinutes = $state<number | string>(initialDraft.flexSlotMinutes)
  let flexSlotsPerDay = $state<number | string>(initialDraft.flexSlotsPerDay)
  let customerAppointmentWindowDeltaMinutes = $state<number | string>(
    initialDraft.customerAppointmentWindowDeltaMinutes,
  )
  let customerAppointmentWindowRoundingMinutes = $state<number | string>(
    initialDraft.customerAppointmentWindowRoundingMinutes,
  )
  let appointmentLetterLeadDays = $state<number | string>(initialDraft.appointmentLetterLeadDays)
  let appointmentLetterAutoDispatchEnabled = $state(
    initialDraft.appointmentLetterAutoDispatchEnabled,
  )
  let appointmentLetterTodoAssignmentMode = $state<AppointmentLetterTodoAssignmentMode>(
    initialDraft.appointmentLetterTodoAssignmentMode,
  )
  let lastSaved = $state(initialDraft)
  let error = $state<string | null>(null)
  let notice = $state<string | null>(null)
  const disabled = $derived(saving || !canManage)
  const automaticDispatchHelp =
    "Gilt nach dem Speichern als Vorauswahl für neue Ankündigungspläne. Bereits gespeicherte Pläne bleiben unverändert – auch beim Ausschalten. Automatisch vorgesehene persönliche Briefe werden bei Fälligkeit ohne weitere Einzelprüfung kostenpflichtig beauftragt, sofern sie noch erforderlich sind. Die Auswahl kann je neuem Plan geändert werden. Versandprobleme erscheinen als Inbox-Aufgabe. Es gibt keine zusätzliche Kostenobergrenze."
  const automaticDispatchTooltip = $derived(
    settings.appointment_letter_auto_dispatch_disabled_reason
      ? `${settings.appointment_letter_auto_dispatch_disabled_reason} ${automaticDispatchHelp}`
      : automaticDispatchHelp,
  )

  const currentDraft = () => ({
    flexSlotMinutes,
    flexSlotsPerDay,
    customerAppointmentWindowDeltaMinutes,
    customerAppointmentWindowRoundingMinutes,
    appointmentLetterLeadDays,
    appointmentLetterAutoDispatchEnabled,
    appointmentLetterTodoAssignmentMode,
  })

  const hasChanges = () => {
    const current = currentDraft()

    return (
      current.appointmentLetterAutoDispatchEnabled !==
        lastSaved.appointmentLetterAutoDispatchEnabled ||
      String(current.flexSlotMinutes) !== String(lastSaved.flexSlotMinutes) ||
      String(current.flexSlotsPerDay) !== String(lastSaved.flexSlotsPerDay) ||
      String(current.customerAppointmentWindowDeltaMinutes) !==
        String(lastSaved.customerAppointmentWindowDeltaMinutes) ||
      String(current.customerAppointmentWindowRoundingMinutes) !==
        String(lastSaved.customerAppointmentWindowRoundingMinutes) ||
      String(current.appointmentLetterLeadDays) !== String(lastSaved.appointmentLetterLeadDays) ||
      String(current.appointmentLetterTodoAssignmentMode) !==
        String(lastSaved.appointmentLetterTodoAssignmentMode)
    )
  }

  const dirty = $derived(hasChanges())
  const setDraft = (next: ReturnType<typeof readDraft>) => {
    flexSlotMinutes = next.flexSlotMinutes
    flexSlotsPerDay = next.flexSlotsPerDay
    customerAppointmentWindowDeltaMinutes = next.customerAppointmentWindowDeltaMinutes
    customerAppointmentWindowRoundingMinutes = next.customerAppointmentWindowRoundingMinutes
    appointmentLetterLeadDays = next.appointmentLetterLeadDays
    appointmentLetterAutoDispatchEnabled = next.appointmentLetterAutoDispatchEnabled
    appointmentLetterTodoAssignmentMode = next.appointmentLetterTodoAssignmentMode
  }
  const save = async () => {
    if (!active || !canManage || !dirty || saving) return

    const targetOrganization = organizationId
    const payload = buildPlanningSettingsPayload(currentDraft(), settings)
    error = null
    notice = null

    if (payload.flex_slot_minutes * payload.flex_slots_per_day > 480) {
      error = "Flextermine passen nicht in einen 8h-Tag. Bitte Anzahl oder Dauer anpassen."

      return
    }

    saving = true
    try {
      if (
        payload.appointment_letter_auto_dispatch_enabled &&
        !lastSaved.appointmentLetterAutoDispatchEnabled
      ) {
        const approved = await newApproval({
          title: "Automatischen Briefversand als Standard speichern?",
          description:
            "Neue Ankündigungspläne erhalten automatischen Briefversand als Vorauswahl. Bei Fälligkeit werden die bestätigten persönlichen Empfänger kostenpflichtig angeschrieben, sofern der Brief noch erforderlich ist. Bereits gespeicherte Pläne bleiben unverändert. Es gibt keine zusätzliche Kostenobergrenze.",
          approvalLabel: "Speichern",
          declineLabel: "Abbrechen",
        })

        if (!approved || !active || organizationId !== targetOrganization || !canManage) return
      }

      const updatedSettings = await saveSettings(payload)

      if (!active || organizationId !== targetOrganization) return

      lastSaved = readDraft(updatedSettings)
      setDraft(lastSaved)
      onSettingsUpdated(updatedSettings)
      notice = "Termineinstellungen gespeichert."
    } catch {
      if (active && organizationId === targetOrganization) {
        error =
          "Termineinstellungen konnten nicht gespeichert werden. Ihre Änderungen bleiben erhalten."
      }
    } finally {
      if (active) saving = false
    }
  }

  onMount(() => {
    const unregister = registerNavigationGuard(
      `planning-settings-${organizationId}`,
      (_to, _options, resume) => {
        if (saving) return false

        if (!dirty) return true

        void newApproval({
          title: "Ungespeicherte Termineinstellungen",
          description: "Möchten Sie die Seite verlassen und Ihre Änderungen verwerfen?",
          approvalLabel: "Verwerfen",
          declineLabel: "Weiter bearbeiten",
          approvalCallback: () => {
            if (!active || saving) return

            setDraft(lastSaved)
            resume?.()
          },
        })

        return false
      },
    )
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (dirty || saving) {
        event.preventDefault()
        event.returnValue = ""
      }
    }
    window.addEventListener("beforeunload", beforeUnload)

    return () => {
      unregister()
      window.removeEventListener("beforeunload", beforeUnload)
    }
  })
</script>

<div class="general-settings-stack">
  <SectionTitle
    title="Termineinstellungen"
    style="margin-bottom: 0.5rem;"
    brightBackground={true}
    small={true}
    helpModalTextId="planningGeneralSettings"
  />
  <div class="box-content form">
    <div class="field-block">
      <Input
        type="select"
        placeholder="Terminbriefe standardmäßig automatisch versenden"
        bind:value={
          () => String(appointmentLetterAutoDispatchEnabled),
          (value: string) => (appointmentLetterAutoDispatchEnabled = value === "true")
        }
        options={[
          { value: "false", label: "Deaktiviert" },
          { value: "true", label: "Aktiviert" },
        ]}
        required={true}
        brightBackground={true}
        disabled={disabled ||
          (!lastSaved.appointmentLetterAutoDispatchEnabled &&
            Boolean(settings.appointment_letter_auto_dispatch_disabled_reason))}
        tooltip={automaticDispatchTooltip}
      />
    </div>
    <div class="settings-row">
      <div class="field-block">
        <Input
          type="select"
          placeholder="Terminbrief-Vorlauf"
          bind:value={appointmentLetterLeadDays}
          options={withCurrentNumberOption(
            appointmentLetterLeadDayOptions,
            Number(lastSaved.appointmentLetterLeadDays),
          )}
          brightBackground={true}
          {disabled}
          tooltip="Gezählt wird rückwärts ab Montag der Terminwoche – unabhängig vom Termintag. Bei Automatik erfolgt die Beauftragung im nächsten erfolgreichen Fälligkeitsscan, sonst entsteht eine Aufgabe."
          helperText={appointmentLetterLeadExample(
            appointmentLetterLeadDays,
            appointmentLetterAutoDispatchEnabled,
          )}
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Terminbrief-To-dos"
          bind:value={appointmentLetterTodoAssignmentMode}
          options={appointmentLetterTodoAssignmentModeOptions}
          brightBackground={true}
          {disabled}
          tooltip="Pro Zustellwoche eine gemeinsame Aufgabe oder eine je Mitarbeiter. Ohne zugewiesenen Mitarbeiter erhält der Administrator die Aufgabe."
          helperText="Steuert, wem postalische Terminbrief-To-dos in der Inbox zugewiesen werden."
        />
      </div>
    </div>

    <div class="settings-row">
      <div class="field-block">
        <Input
          type="select"
          placeholder="Flextermine pro Tag"
          bind:value={flexSlotsPerDay}
          options={withCurrentNumberOption(
            flexSlotsPerDayOptions,
            Number(lastSaved.flexSlotsPerDay),
          )}
          brightBackground={true}
          {disabled}
          tooltip="Maximale Anzahl an Flexterminen pro Tag."
          helperText="Begrenzt, wie viele zusätzliche Flextermine pro Tag angeboten werden."
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Flextermin max. Dauer"
          bind:value={flexSlotMinutes}
          options={withCurrentNumberOption(
            flexSlotMinuteOptions,
            Number(lastSaved.flexSlotMinutes),
          )}
          brightBackground={true}
          {disabled}
          tooltip="Maximale Dauer eines Flextermins."
          helperText="Begrenzt die Dauer eines einzelnen Flextermins in Minuten."
        />
      </div>
    </div>

    <div class="settings-row">
      <div class="field-block">
        <Input
          type="select"
          placeholder="Zeitfenster-Puffer"
          bind:value={customerAppointmentWindowDeltaMinutes}
          options={withCurrentNumberOption(
            customerAppointmentWindowDeltaOptions,
            Number(lastSaved.customerAppointmentWindowDeltaMinutes),
          )}
          brightBackground={true}
          {disabled}
          tooltip="Erweitert harte Termine nur in der Kundenkommunikation. Der Wert wird zur Hälfte vor und nach dem Termin addiert."
          helperText="Flextermine nutzen ihr eigenes Zeitfenster und bekommen keinen zusätzlichen Puffer."
        />
      </div>
      <div class="field-block">
        <Input
          type="select"
          placeholder="Zeitfenster-Rundung"
          bind:value={customerAppointmentWindowRoundingMinutes}
          options={withCurrentNumberOption(
            customerAppointmentWindowRoundingOptions,
            Number(lastSaved.customerAppointmentWindowRoundingMinutes),
          )}
          brightBackground={true}
          {disabled}
          tooltip="Rundet das Kunden-Zeitfenster nach außen: Beginn nach unten, Ende nach oben. Der Kalendertermin bleibt unverändert."
          helperText="Der Puffer gilt nur für feste Termine. Die Rundung gilt für feste Termine und Flextermine. Beispiel: 15:45-16:15 wird bei Stundenrundung als 15:00-17:00 kommuniziert."
        />
      </div>
    </div>
  </div>
  {#if error}<p role="alert">{error}</p>{/if}
  {#if notice}<p role="status">{notice}</p>{/if}
  {#if canManage}
    <div class="save-actions">
      <CTA
        type="secondaryButton"
        content="Zurücksetzen"
        onClick={() => {
          setDraft(lastSaved)
          error = null
          notice = null
        }}
        disabled={saving || !dirty}
      />
      <CTA
        type="primaryButton"
        content="Speichern"
        icon={mdiContentSave}
        onClick={save}
        disabled={saving || !dirty}
      />
    </div>
  {/if}
</div>

<style lang="less">
  .general-settings-stack {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .box-content {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .settings-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(13rem, 1fr));
    gap: 0.9rem;
  }

  .field-block {
    min-width: 0;
  }
  .save-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
  }
  [role="alert"] {
    border-left: 3px solid var(--red-300);
    padding: 0.6rem 0.8rem;
  }
</style>
